// Auto fix (§11.1) and match exposure (§11.7). Pure functions over a small LINEAR P3 image (≈512 px for
// auto fix, 256 px for match exposure), using the CPU reference pipeline so the numbers match the
// renderer. Deterministic.
import { lumaP3, apply, type Vec3 } from '../color/matrix';
import { wbMatrix, solveWb } from '../color/wb';
import { p3ToOklab, toLch } from '../color/oklab';
import { linearToSrgb } from '../color/transfer';
import { shoulder, toneY } from '../color/tone';
import { cpuColor, type LinearImage } from '../engine/cpuPipeline';
import type { Settings } from '../model/types';

export type AutoFixResult = {
  temp: number;
  tint: number;
  exposure: number;
  contrast: number;
  whites: number;
  blacks: number;
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function percentile(sorted: Float64Array, p: number): number {
  if (!sorted.length) return 0;
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(p * (sorted.length - 1))))]!;
}

/** Fraction of pixels whose max (or min) encoded channel clips after the whites (or blacks) curve. */
function clipFraction(vals: Float32Array, k: number, which: 'whites' | 'blacks'): number {
  let n = 0;
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i]!;
    const o = which === 'whites' ? v + (k / 100) * 0.2 * v ** 4 : v + (k / 100) * 0.12 * (1 - v) ** 4;
    if (which === 'whites' ? o >= 1 : o <= 0) n++;
  }
  return n / vals.length;
}

/** Binary search for the slider value giving `target` clipped fraction (monotone in the value). */
function solveClip(vals: Float32Array, which: 'whites' | 'blacks', target = 0.001): number {
  // whites: clipping grows with k; blacks: clipping grows as k decreases.
  const f = (k: number) => clipFraction(vals, k, which);
  let lo = -100,
    hi = 100;
  if (which === 'whites') {
    if (f(hi) < target) return 100;
    if (f(lo) > target) return -100;
  } else {
    if (f(lo) < target) return -100;
    if (f(hi) > target) return 100;
  }
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    const tooMuch = f(mid) > target;
    // whites: too much clipping → lower the value; blacks: too much clipping → raise it.
    if (tooMuch === (which === 'whites')) hi = mid;
    else lo = mid;
  }
  return Math.round((lo + hi) / 2);
}

/**
 * §11.1. `img` is the photo rendered at ~512 px with its current geometry and everything else zero
 * (linear P3, straight alpha). Steps: gray-world WB at 70% strength → exposure from the geometric mean
 * → contrast +10 for flat images → whites/blacks so 0.1% clip at each end.
 */
/**
 * Gray-world white balance (§11.1) over mid-tone, low-chroma pixels; `strength` 0.7 for auto fix (food stays
 * appetizing), 1 for the Simple-mode "Fix colors" cleaner.
 */
export function grayWorldWb(img: LinearImage, strength: number): { temp: number; tint: number } {
  const d = img.data;
  const n = img.width * img.height;
  // ── WB: gray-world over mid-tone, low-chroma pixels ──
  const cand: number[] = [];
  const chroma: number[] = [];
  for (let p = 0; p < n; p++) {
    if (d[p * 4 + 3]! < 0.5) continue;
    const r = d[p * 4]!,
      g = d[p * 4 + 1]!,
      b = d[p * 4 + 2]!;
    const Y = lumaP3(r, g, b);
    if (Y <= 0.05 || Y >= 0.9) continue;
    cand.push(p);
    chroma.push(toLch(p3ToOklab([r, g, b]))[1]);
  }
  let temp = 0,
    tint = 0;
  if (cand.length >= 16) {
    const sortedC = Float64Array.from(chroma).sort();
    const c80 = percentile(sortedC, 0.8);
    let R = 0,
      G = 0,
      B = 0,
      k = 0;
    cand.forEach((p, i) => {
      if (chroma[i]! > c80) return;
      R += d[p * 4]!;
      G += d[p * 4 + 1]!;
      B += d[p * 4 + 2]!;
      k++;
    });
    if (k > 0) {
      const s = solveWb([R / k, G / k, B / k]);
      temp = Math.round(s.temp * strength);
      tint = Math.round(s.tint * strength);
    }
  }
  return { temp, tint };
}

export function autoFix(img: LinearImage): AutoFixResult {
  const d = img.data;
  const n = img.width * img.height;
  const { temp, tint } = grayWorldWb(img, 0.7);
  // ── Exposure from the geometric mean of luminance after WB ──
  const wb = wbMatrix(temp, tint);
  const Ys = new Float64Array(n);
  let logSum = 0,
    cnt = 0;
  for (let p = 0; p < n; p++) {
    const c = apply(wb, [d[p * 4]!, d[p * 4 + 1]!, d[p * 4 + 2]!]);
    const Y = Math.max(lumaP3(c[0], c[1], c[2]), 1e-4);
    Ys[p] = Y;
    logSum += Math.log2(Y);
    cnt++;
  }
  const geomean = 2 ** (logSum / Math.max(1, cnt));
  const exposure = Math.round(clamp(0.8 * Math.log2(0.18 / geomean), -2, 2) * 100) / 100;
  // ── Contrast for flat images ──
  const mean = logSum / Math.max(1, cnt);
  let varSum = 0;
  for (let p = 0; p < n; p++) varSum += (Math.log2(Ys[p]!) - mean) ** 2;
  const contrast = Math.sqrt(varSum / Math.max(1, cnt)) < 1.5 ? 10 : 0;
  // ── Whites / blacks on the encoded max / min channel after WB, exposure, contrast, shoulder ──
  const gain = 2 ** exposure;
  const vmax = new Float32Array(n),
    vmin = new Float32Array(n);
  for (let p = 0; p < n; p++) {
    let c: Vec3 = apply(wb, [d[p * 4]!, d[p * 4 + 1]!, d[p * 4 + 2]!]);
    c = [c[0] * gain, c[1] * gain, c[2] * gain];
    if (contrast) {
      const Y = Math.max(lumaP3(c[0], c[1], c[2]), 1e-6);
      const k = toneY(Y, Y, { highlights: 0, shadows: 0, contrast }) / Y;
      c = [c[0] * k, c[1] * k, c[2] * k];
    }
    const e = c.map((x) => linearToSrgb(Math.max(0, shoulder(x))));
    vmax[p] = Math.max(e[0]!, e[1]!, e[2]!);
    vmin[p] = Math.min(e[0]!, e[1]!, e[2]!);
  }
  return {
    temp,
    tint,
    exposure,
    contrast,
    whites: solveClip(vmax, 'whites'),
    blacks: solveClip(vmin, 'blacks'),
  };
}

/** Writes an auto-fix result into settings (the values land in the sliders and stay editable). */
export function applyAutoFix(s: Settings, r: AutoFixResult): Settings {
  const out = structuredClone(s);
  out.wb.temp = r.temp;
  out.wb.tint = r.tint;
  out.light.exposure = r.exposure;
  out.light.contrast = r.contrast;
  out.light.whites = r.whites;
  out.light.blacks = r.blacks;
  return out;
}

/** Log-average luminance (log2) of `img` rendered with `s` but exposure 0 (§11.7). */
export function logAverage(img: LinearImage, s: Settings): number {
  const noExp = structuredClone(s);
  noExp.light.exposure = 0;
  const out = cpuColor(img, noExp);
  let sum = 0,
    n = 0;
  for (let p = 0; p < out.width * out.height; p++) {
    if (out.data[p * 4 + 3]! < 0.5) continue;
    sum += Math.log2(Math.max(lumaP3(out.data[p * 4]!, out.data[p * 4 + 1]!, out.data[p * 4 + 2]!), 1e-5));
    n++;
  }
  return n ? sum / n : Math.log2(0.18);
}

/** §11.7: exposure = clamp(ref − mean + refExposure, ±5) for each other photo. */
export function matchExposure(
  ref: { img: LinearImage; settings: Settings },
  others: { img: LinearImage; settings: Settings }[],
): number[] {
  const refMean = logAverage(ref.img, ref.settings);
  return others.map(
    (o) =>
      Math.round(clamp(refMean - logAverage(o.img, o.settings) + ref.settings.light.exposure, -5, 5) * 100) /
      100,
  );
}
